/**
 * batch-50 reistijd-geocode (FO-403) - geocode-querybouw + kwaliteitsgate.
 *
 * Pure helpers, bewust in `@streep/utils` zodat zowel de API (`GoogleApiService`)
 * als de sanerings-/scan-scripts in `packages/db/prisma/scripts` exact dezelfde
 * regels gebruiken. Geen fetch, geen DB, geen deps.
 *
 * Het probleem dat dit dicht (PO-incidentanalyse 24-07-2026):
 *
 *   Gat A - huisnummer viel weg. De oude querybouw was
 *   `straatnaam && huisnummer ? "straat nr" : ''`, dus zonder straatnaam
 *   verdween ook het huisnummer. Het reisuren-pad geocodeert bewust op
 *   postcode + huisnummer (`street: ''`), dus Google kreeg letterlijk
 *   "5330AB, Nederland".
 *
 *   Gat B - geen kwaliteitscheck. Google antwoordt op een onbekende postcode
 *   met een partial match op het land: result-type `country`, coordinaat = het
 *   geografische midden van Nederland (bij Amersfoort). Dat werd ongezien als
 *   geldige coordinaat gebruikt, waardoor er stil een route naar het landmidden
 *   werd berekend i.p.v. een zichtbare schatting.
 */

/**
 * Result-types die we als bruikbaar adresniveau accepteren.
 *
 * `postal_code` hoort er bewust bij: een NL-postcode is een straatsegment, dus
 * postcode-niveau is in NL feitelijk adresniveau (spec §6.1 + aanname A1). Een
 * postcode-only geocode mag dus niet afvallen.
 */
export const GEOCODE_GEACCEPTEERDE_TYPES = [
  'street_address',
  'premise',
  'subpremise',
  'route',
  'postal_code',
] as const

export type GeocodeGeaccepteerdType = (typeof GEOCODE_GEACCEPTEERDE_TYPES)[number]

/**
 * Types die expliciet te grof zijn (land/provincie/gemeente/postcode-prefix).
 * Puur voor de logging + rapportage: de gate werkt op de whitelist hierboven,
 * zodat een onbekend nieuw Google-type ook niet stil doorglipt.
 */
export const GEOCODE_TE_GROVE_TYPES = [
  'country',
  'postal_code_prefix',
  'administrative_area_level_1',
  'administrative_area_level_2',
  'administrative_area_level_3',
  'administrative_area_level_4',
  'administrative_area_level_5',
] as const

export type GeocodeAfkeurReden = 'PARTIAL_MATCH' | 'TYPE_TE_GROF'

export interface GeocodeKwaliteitInvoer {
  /** Google `partial_match` op het beoordeelde resultaat. */
  partialMatch?: boolean | null
  /** Google `types` op het beoordeelde resultaat. */
  types?: readonly string[] | null
  /**
   * NO-OP sinds de partial-match-correctie hieronder; alleen nog aanwezig zodat
   * de bestaande aanroepers (kaart-pad `ensureGeocoded`) blijven compileren.
   * De type-whitelist beslist nu voor beide paden, en die accepteert het geval
   * waarvoor deze vlag ooit bedoeld was (straatadres met afwijkende postcode)
   * sowieso. Opruimen = losse follow-up.
   */
  partialMatchToegestaan?: boolean
}

export interface GeocodeKwaliteitOordeel {
  geaccepteerd: boolean
  /** null wanneer geaccepteerd. */
  reden: GeocodeAfkeurReden | null
  /** De beoordeelde types (genormaliseerd naar een array) - voor logging. */
  types: string[]
  partialMatch: boolean
}

/** True als dit type als "te grof" bekendstaat (land/provincie/gemeente/prefix). */
export function isTeGrofGeocodeType(type: string): boolean {
  return (GEOCODE_TE_GROVE_TYPES as readonly string[]).includes(type)
}

/**
 * Kwaliteitsgate op een geocode-resultaat (spec §3.B).
 *
 * Afgekeurd wanneer geen enkel type uit de whitelist voorkomt: land-, provincie-
 * of gemeenteniveau, een postcode-prefix, of een type dat geen adresniveau is.
 *
 * Whitelist-wins is bewust strenger dan een zwarte lijst: een nieuw of onbekend
 * Google-type (bv. `plus_code`) levert geen bruikbaar adrespunt en mag niet stil
 * als route-eindpunt gebruikt worden. Afkeuren is veilig - de aanroeper valt dan
 * terug op de luchtlijn-schatting vanaf de kaart-coordinaten.
 *
 * `partial_match` is BEWUST GEEN afkeurgrond meer (integratie batch-50, gemeten
 * op de prod-cache). Google zet die vlag op ELKE NL postcode+huisnummer-geocode,
 * ook als het antwoord perfect klopt:
 *
 *   "20, 5437AS, NL"  -> partial_match: true, types: [postal_code] -> Beers      (correct)
 *   "41, 5361KB, NL"  -> partial_match: true, types: [postal_code] -> Grave      (correct)
 *   "2, 5330AB, NL"   -> partial_match: true, types: [administrative_area_level_1]
 *                        -> Newfoundland, Canada                                 (de bug)
 *
 * Met partial_match als zelfstandige afkeurgrond viel dus 612 van de 612
 * cache-rijen af (de saneringsscript-dry-run op prod flagde de HELE cache) en zou
 * elke reistijd stil op een luchtlijn-schatting uitkomen - terwijl spec §6.1 en
 * aanname A1 juist voorschrijven dat een postcode-niveau-geocode in NL prima is,
 * en acceptatiecriterium 3 een ECHTE route van ~47,6 km verwacht. De type-
 * whitelist vangt de werkelijke foutcase (zie 5330AB hierboven) op zichzelf al.
 * `partialMatch` blijft wel in het oordeel staan voor logging/rapportage.
 */
export function beoordeelGeocodeResultaat(
  invoer: GeocodeKwaliteitInvoer,
): GeocodeKwaliteitOordeel {
  const types = [...(invoer.types ?? [])].filter((t): t is string => typeof t === 'string')
  const partialMatch = invoer.partialMatch === true

  const heeftGeaccepteerdType = types.some((t) =>
    (GEOCODE_GEACCEPTEERDE_TYPES as readonly string[]).includes(t),
  )
  if (!heeftGeaccepteerdType) {
    // Zonder bruikbaar type is een partial match de sprekendste verklaring
    // ("Google vond het gevraagde adres niet"); zonder die vlag is het simpelweg
    // een te grof antwoord. Puur een rapportage-onderscheid.
    return {
      geaccepteerd: false,
      reden: partialMatch ? 'PARTIAL_MATCH' : 'TYPE_TE_GROF',
      types,
      partialMatch,
    }
  }
  return { geaccepteerd: true, reden: null, types, partialMatch }
}

export interface GeocodeQueryAdres {
  straatnaam?: string | null
  huisnummer?: string | null
  postcode?: string | null
  plaats?: string | null
  /** Landcode of landnaam; default 'NL'. */
  landcode?: string | null
}

/**
 * Bouw de Google-geocode-query (spec §3.A).
 *
 * Vier combinaties, en in alle vier blijft het huisnummer behouden:
 *   1. straat + nr  -> "Hoofdstraat 12, 1234AB, Amsterdam, NL"
 *   2. alleen nr    -> "12, 1234AB, NL"          (het reisuren-pad)
 *   3. alleen straat-> "Hoofdstraat, 1234AB, Amsterdam, NL"
 *   4. geen van bei.-> "1234AB, Amsterdam, NL"
 *
 * NL-postcodes zijn samen met het huisnummer uniek, dus combinatie 2 resolvet
 * met deze querybouw op adres- of postcode-niveau in plaats van op het land.
 */
export function bouwGeocodeQuery(addr: GeocodeQueryAdres): string {
  const straat = (addr.straatnaam ?? '').trim()
  const nr = (addr.huisnummer ?? '').trim()
  const straatDeel = straat && nr ? `${straat} ${nr}` : straat || nr
  return [
    straatDeel,
    (addr.postcode ?? '').trim(),
    (addr.plaats ?? '').trim(),
    (addr.landcode ?? '').trim() || 'NL',
  ]
    .filter((deel) => deel.length > 0)
    .join(', ')
}

// ─── Landmidden-detectie (cache-sanering, spec §3.C) ─────────────────────────

export interface Coordinaat {
  lat: number
  lng: number
}

/**
 * Google's centroid voor het `country`-resultaat "Nederland" - het punt waar een
 * afgekeurde postcode vroeger op uitkwam.
 *
 * Bewust ALLEEN dit punt en met een krappe radius: Google geeft bij een
 * country-match exact deze coordinaat terug, dus een grotere marge levert geen
 * extra treffers maar wél valse positieven. Een monteur die in Amersfoort of
 * Leusden woont heeft een volstrekt geldig adres een paar kilometer verderop;
 * die mag nooit als "landmidden" worden aangemerkt en zijn routes verliezen.
 */
export const NL_LANDMIDDEN_PUNTEN: readonly Coordinaat[] = [
  { lat: 52.132633, lng: 5.291266 },
]

/** Standaard-radius (km) waarbinnen een coordinaat als landmidden telt. */
export const LANDMIDDEN_RADIUS_KM = 1

const AARDSTRAAL_KM = 6371

/** Haversine-afstand in km tussen twee coordinaten (hemelsbreed). */
export function coordinaatAfstandKm(a: Coordinaat, b: Coordinaat): number {
  const rad = (d: number): number => (d * Math.PI) / 180
  const dLat = rad(b.lat - a.lat)
  const dLng = rad(b.lng - a.lng)
  const x =
    Math.sin(dLat / 2) ** 2 +
    Math.sin(dLng / 2) ** 2 * Math.cos(rad(a.lat)) * Math.cos(rad(b.lat))
  return 2 * AARDSTRAAL_KM * Math.asin(Math.sqrt(x))
}

/**
 * True wanneer een coordinaat binnen `radiusKm` van een bekend landmidden-punt
 * ligt - het handtekening-symptoom van een afgekeurde/partial geocode uit de
 * oude code. Gebruikt door de cache-sanering om vervuilde routes te herkennen.
 */
export function isLandmiddenCoordinaat(
  coord: Coordinaat,
  radiusKm: number = LANDMIDDEN_RADIUS_KM,
): boolean {
  return NL_LANDMIDDEN_PUNTEN.some((punt) => coordinaatAfstandKm(coord, punt) <= radiusKm)
}
